import { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import { healthOverviewChartOptions } from "../../DashboardPage/components/chartsOptions"
import { tooltipLineWithPoints } from "../../DashboardPage/components/chartsPlagins"
import ChartLegend from "../../Health/components/ChartLegend/ChartLegend"
import DataSourceSwitcher from "../../Health/components/DataSourceSwitcher/DataSourceSwitcher"
import PermissionPlaceholder from "../../Health/components/PermissionPlaceholder"
import { HOME_CHART } from "../redux/home.constants"
import {
  selectHomeChartFilters,
  selectHomeRateChart,
  selectHomeRateChartFetching,
  setChartFilters
} from "../redux/home.slice"
import { buildRateChartData, getRateLegendItems, RATE_SERIES } from "../utils/rateChartData"
import ChartUnavailableBanner from "./ChartUnavailableBanner"
import HomeChartCard from "./HomeChartCard"

import styles from "../styles.module.less"

const SEGMENTS = [
  { value: "rate", label: "Rate" },
  { value: "count", label: "Count" }
]

// Asked at `rate` and only at `rate`: that answer carries the percentages and the raw counts side
// by side, so the toggle is a choice between series already in hand, not a new request.
const RateChart = ({ canReadHealth, healthWarning, getChartData, getMainInfo, dataSource }) => {
  const dispatch = useDispatch()

  const { t } = useTranslation()
  const [activeSeries, setActiveSeries] = useState(() => new Set(Object.values(RATE_SERIES)))

  const { mode, source } = useSelector(selectHomeChartFilters(HOME_CHART.RATE))
  const overview = useSelector(selectHomeRateChart)
  const fetching = useSelector(selectHomeRateChartFetching)
  const theme = useSelector((state) => state.nav.theme)

  // `mode` is deliberately not asked for — the `rate` answer carries both series — but `source` is:
  // the card's own switcher is what picks it, and the page's default stands until it is touched.
  useEffect(() => {
    if (!canReadHealth || healthWarning) return
    getChartData({ chart_type: "rate", source: source ?? dataSource })
  }, [getChartData, canReadHealth, source, dataSource, healthWarning])

  // The banner main-info feeds is about the processor, not about the window, so this asks on mount
  // and on a source change only — `getMainInfo` is left out of the deps because its identity
  // changes with the page's period.
  useEffect(() => {
    if (!canReadHealth) return
    getMainInfo({ source: source ?? dataSource })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReadHealth, source, dataSource])

  const data = useMemo(
    () => buildRateChartData(overview, mode, activeSeries),
    [overview, mode, activeSeries]
  )
  const options = useMemo(() => healthOverviewChartOptions(theme), [theme])

  // Only this card gets a legend, and only while a comparison is actually plotted — with no
  // previous-period series there is nothing the colours need to be told apart from.
  const hasComparison = Boolean(overview?.comparison?.result)
  const legendItems = useMemo(
    () => (hasComparison ? getRateLegendItems(data) : []),
    [hasComparison, data]
  )

  const toggleSeries = useCallback((id) => {
    setActiveSeries((previous) => {
      const next = new Set(previous)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  if (!canReadHealth) {
    return (
      <div className={styles.card} data-testid="home-chart-rate">
        <PermissionPlaceholder minHeight={320} />
      </div>
    )
  }

  return (
    <HomeChartCard
      testId="home-chart-rate"
      icon="centralhub"
      title="Chargeback rate"
      segments={SEGMENTS}
      activeSegment={mode}
      onSegmentChange={(value) =>
        dispatch(setChartFilters({ chart: HOME_CHART.RATE, filters: { mode: value } }))
      }
      data={data}
      options={options}
      plugins={[tooltipLineWithPoints]}
      loading={fetching}
      filterDisabled={fetching || healthWarning}
      extra={
        <DataSourceSwitcher
          dataSource={source ?? dataSource}
          onSwitch={(value) =>
            dispatch(setChartFilters({ chart: HOME_CHART.RATE, filters: { source: value } }))
          }
          loading={fetching}
        />
      }
      banner={healthWarning ? <ChartUnavailableBanner warning={healthWarning} /> : null}
      footer={
        hasComparison && !healthWarning ? (
          <ChartLegend
            items={legendItems.map((item) => ({ ...item, label: t(item.label) }))}
            isActive={(id) => activeSeries.has(id)}
            onToggle={toggleSeries}
            style={{ marginTop: 12 }}
          />
        ) : null
      }
    />
  )
}

export default RateChart
